import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
    ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { WeekdayStr } from 'rrule';
import { Observable } from 'rxjs';
import { finalize } from 'rxjs/operators';

import { LegFiJwtService } from '../../../services/auth/legfi-jwt.service';
import { GablePendingQuestions } from '../../../services/gable/gable-stream.service';
import { GableQuestionAnswer } from '../../../services/gable/gable.service';
import {
    GableWorkflow,
    GableWorkflowClarifications,
    GableWorkflowInput,
    GableWorkflowIntakeBody,
    GableWorkflowIntakePlan,
    GableWorkflowOutcome,
    GableWorkflowRecipient,
    GableWorkflowRecipientOption,
    GableWorkflowSkill,
    GableWorkflowsService,
    GableWorkflowUpdate,
    normalizeOutcomes,
} from '../../../services/gable/gable-workflows.service';
import {
    buildRrule,
    DEFAULT_RRULE_PICKERS,
    emailRecipients,
    formatInTimezone,
    GABLE_RRULE_FREQUENCIES,
    GABLE_RRULE_MONTH_DAYS,
    GABLE_RRULE_MONTHS,
    GABLE_RRULE_ORDINALS,
    GABLE_RRULE_WEEKDAYS,
    GABLE_RRULE_YEAR_DAYS,
    GableRruleFrequency,
    GableRruleMonthlyMode,
    GableRrulePickers,
    hasOutcome,
    nextOccurrence,
    outcomesText,
    parseRrule,
    rruleText,
} from '../../../services/gable/gable-workflow-format';
import { GrowlerService } from '../../shared/growler/growler.service';
import { FlexSpacerDirective } from '../../v2-layout/shared/directives/flex-spacer.directive';
import { RadioGroupDirective, RequiredMarkerDirective } from '../../v2-layout/shared/directives/form.directive';
import { QuestionCardComponent } from '../../v2-layout/gable/question-card/question-card.component';
import { PeoplePickerComponent } from '../../v2-layout/shared/people-picker/people-picker.component';
import { PICKER_AVATAR_COLOR, PickerPerson, PickerSelection } from '../../v2-layout/shared/people-picker/people-picker.types';

export type GableWorkflowEditorStep = 'when' | 'do' | 'then' | 'review';
export type GableWorkflowReviewPhase = 'questions' | 'plan' | 'waiting';
export type GableWorkflowDoMode = 'describe' | 'skill';

export const GABLE_WORKFLOW_STEPS: { key: GableWorkflowEditorStep; label: string }[] = [
    { key: 'when', label: 'When' },
    { key: 'do', label: 'Do' },
    { key: 'then', label: 'Then' },
    { key: 'review', label: 'Review' },
];

interface DoForm {
    intent: FormControl<string>;
    skillName: FormControl<string | null>;
}

interface ThenForm {
    suggestions: FormControl<boolean>;
    email: FormControl<boolean>;
}

interface ReviewForm {
    title: FormControl<string>;
}

interface PickersForm {
    frequency: FormControl<GableRruleFrequency>;
    weekdays: FormControl<WeekdayStr[]>;
    monthlyMode: FormControl<GableRruleMonthlyMode>;
    monthDay: FormControl<number>;
    ordinal: FormControl<number>;
    weekday: FormControl<WeekdayStr>;
    month: FormControl<number>;
    yearDay: FormControl<number>;
    time: FormControl<string>;
}

interface ClarificationRound {
    batch: GablePendingQuestions;
    answers: GableQuestionAnswer[];
}

/** Picker ids: membership ids as they are, anyone without one (the requesting user) as a negative user id so the two never collide. */
export function recipientPickerId(r: GableWorkflowRecipient): number {
    return typeof r.membershipId === 'number' && r.membershipId > 0 ? r.membershipId : -(r.userId ?? 0);
}

function recipientFromPickerId(id: number, name: string, email: string): GableWorkflowRecipient {
    return id > 0 ? { membershipId: id, name, email } : { userId: -id, name, email };
}

export function recipientFromOption(o: GableWorkflowRecipientOption): GableWorkflowRecipient | null {
    if (o.membershipId !== null && o.membershipId !== undefined) {
        return { membershipId: o.membershipId, name: o.name, email: o.email };
    }
    if (o.userId !== null && o.userId !== undefined) {
        return { userId: o.userId, name: o.name, email: o.email };
    }
    return null;
}

const EMAIL_NOTICE = 'You asked for an email. Pick who should receive it.';
const WAITING_HINTS = ['Reading your answers', 'Checking the schedule', 'Putting the summary into plain words'];
const WAITING_HINT_MS = 4000;

const INTENT_PLACEHOLDER = 'Email the board a summary of our financials and flag anything unusual';
const FOCUS_SELECTOR = '.step-body [data-autofocus], .step-body textarea, .step-body input:not([type="hidden"]), .step-body mat-select, .step-body button';
const FOCUS_FALLBACK_SELECTOR = '.step-body .question-card';

/** In-panel When -> Do -> Then -> Review editor for one Gable workflow; edit mode opens at Review. */
@UntilDestroy()
@Component({
    selector: 'app-gable-workflow-editor',
    templateUrl: './gable-workflow-editor.component.html',
    styleUrls: ['./gable-workflow-editor.component.scss'],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        MatButtonModule,
        MatCheckboxModule,
        MatFormFieldModule,
        MatInputModule,
        MatSelectModule,
        MatRadioModule,
        FlexSpacerDirective,
        RadioGroupDirective,
        RequiredMarkerDirective,
        QuestionCardComponent,
        PeoplePickerComponent,
    ],
})
export class GableWorkflowEditorComponent implements OnInit, OnDestroy {
    @Input() workflow: GableWorkflow | null = null;
    @Input() organizationId?: number;

    @Output() readonly saved = new EventEmitter<GableWorkflow>();
    @Output() readonly cancel = new EventEmitter<void>();

    @ViewChild(PeoplePickerComponent) private _picker?: PeoplePickerComponent;

    readonly steps = GABLE_WORKFLOW_STEPS;
    readonly intentPlaceholder = INTENT_PLACEHOLDER;
    readonly timezone = LegFiJwtService.getTimezone();
    readonly frequencies = GABLE_RRULE_FREQUENCIES;
    readonly weekDays = GABLE_RRULE_WEEKDAYS;
    readonly ordinals = GABLE_RRULE_ORDINALS;
    readonly monthDays = GABLE_RRULE_MONTH_DAYS;
    readonly months = GABLE_RRULE_MONTHS;
    readonly yearDays = GABLE_RRULE_YEAR_DAYS;

    isEdit = false;
    step: GableWorkflowEditorStep = 'when';
    reviewPhase: GableWorkflowReviewPhase = 'plan';
    furthest = 0;

    pickersForm!: FormGroup<PickersForm>;
    doForm!: FormGroup<DoForm>;
    thenForm!: FormGroup<ThenForm>;
    reviewForm!: FormGroup<ReviewForm>;

    rrule = '';
    recipients: GableWorkflowRecipient[] = [];
    recipientPeople: PickerPerson[] | null = null;
    recipientsLoading = false;
    recipientsLoadFailed = false;

    doMode: GableWorkflowDoMode = 'describe';
    skills: GableWorkflowSkill[] = [];
    skillsLoading = false;
    skillsLoadFailed = false;

    batch: GablePendingQuestions | null = null;
    plan: GableWorkflowIntakePlan | null = null;
    intakeBusy = false;
    waitingTitle = '';
    waitingHintIndex = 0;
    isSaving = false;

    private _recipientByPickerId: Map<number, GableWorkflowRecipient> | null = null;
    private _hintTimer: ReturnType<typeof setInterval> | null = null;
    private _viewBeforeIntake: { step: GableWorkflowEditorStep; reviewPhase: GableWorkflowReviewPhase } | null = null;
    private _pendingRecipientSearch: string | null = null;
    private _draftId: string | null = null;
    private _rounds: ClarificationRound[] = [];
    private _planSignature: string | null = null;
    private _skillsLoaded = false;
    private _savedInterval: { frequency: GableRruleFrequency; interval: number } | null = null;

    constructor(
        private readonly _fb: FormBuilder,
        private readonly _cdr: ChangeDetectorRef,
        private readonly _el: ElementRef<HTMLElement>,
        private readonly _growler: GrowlerService,
        private readonly _workflows: GableWorkflowsService,
    ) {}

    ngOnInit(): void {
        const w = this.workflow;
        this.isEdit = w !== null;
        const parsed = parseRrule(w?.trigger?.rrule);
        const pickers = parsed ?? DEFAULT_RRULE_PICKERS;
        this._savedInterval = parsed ? { frequency: parsed.frequency, interval: parsed.interval } : null;
        this.pickersForm = this._fb.group<PickersForm>({
            frequency: this._fb.nonNullable.control<GableRruleFrequency>(pickers.frequency),
            weekdays: this._fb.nonNullable.control<WeekdayStr[]>(pickers.weekdays, Validators.required),
            monthlyMode: this._fb.nonNullable.control<GableRruleMonthlyMode>(pickers.monthlyMode),
            monthDay: this._fb.nonNullable.control<number>(pickers.monthDay),
            ordinal: this._fb.nonNullable.control<number>(pickers.ordinal),
            weekday: this._fb.nonNullable.control<WeekdayStr>(pickers.weekday),
            month: this._fb.nonNullable.control<number>(pickers.month),
            yearDay: this._fb.nonNullable.control<number>(pickers.yearDay),
            time: this._fb.nonNullable.control<string>(pickers.time, Validators.required),
        });
        this.doForm = this._fb.group<DoForm>({
            intent: this._fb.nonNullable.control<string>(w?.intent ?? '', [Validators.required, Validators.maxLength(2000)]),
            skillName: this._fb.control<string | null>(w?.skill?.name ?? null),
        });
        this.thenForm = this._fb.group<ThenForm>({
            suggestions: this._fb.nonNullable.control<boolean>(w ? hasOutcome(w.outcomes, 'suggestions') : true),
            email: this._fb.nonNullable.control<boolean>(w ? hasOutcome(w.outcomes, 'email') : false),
        });
        this.reviewForm = this._fb.group<ReviewForm>({
            title: this._fb.nonNullable.control<string>(w?.title ?? '', [Validators.required, Validators.maxLength(150)]),
        });
        this.recipients = w ? emailRecipients(w.outcomes) : [];
        this.rrule = w?.trigger?.rrule ?? buildRrule(pickers);
        this.doMode = w?.skill ? 'skill' : 'describe';
        if (this.doMode === 'skill') {
            this.loadSkills();
        }

        this.pickersForm.valueChanges.pipe(untilDestroyed(this)).subscribe(() => {
            this.rrule = buildRrule(this.pickers);
            this._cdr.markForCheck();
        });
        this.thenForm.controls.email.valueChanges.pipe(untilDestroyed(this)).subscribe((on) => {
            if (on) {
                this.loadRecipients();
            }
            this._cdr.markForCheck();
        });

        if (this.isEdit) {
            this.step = 'review';
            this.reviewPhase = 'plan';
            this.furthest = this.steps.length - 1;
            this._planSignature = this._signature();
            if (this.thenForm.controls.email.value) {
                this.loadRecipients();
            }
        }
        this._focusStep();
    }

    ngOnDestroy(): void {
        this._stopWaiting();
    }

    get waitingHint(): string {
        return WAITING_HINTS[this.waitingHintIndex] ?? WAITING_HINTS[0];
    }

    get stepIndex(): number {
        return this.steps.findIndex((s) => s.key === this.step);
    }

    get frequency(): GableRruleFrequency {
        return this.pickersForm.controls.frequency.value;
    }

    get monthlyMode(): GableRruleMonthlyMode {
        return this.pickersForm.controls.monthlyMode.value;
    }

    hasWeekday(day: WeekdayStr): boolean {
        return this.pickersForm.controls.weekdays.value.includes(day);
    }

    toggleWeekday(day: WeekdayStr, checked: boolean): void {
        const current = this.pickersForm.controls.weekdays.value;
        const next = checked ? [...current.filter((d) => d !== day), day] : current.filter((d) => d !== day);
        this.pickersForm.controls.weekdays.setValue(next);
        this.pickersForm.controls.weekdays.markAsTouched();
    }

    /** The saved interval survives only while the frequency it was written for is still selected. */
    get pickers(): GableRrulePickers {
        const v = this.pickersForm.getRawValue();
        const interval = this._savedInterval?.frequency === v.frequency ? this._savedInterval.interval : 1;
        return { ...DEFAULT_RRULE_PICKERS, ...v, interval };
    }

    /** Core's label while the rule matches what core has seen; the client preview otherwise. */
    get cadenceText(): string {
        if (this.workflow && this.workflow.trigger?.rrule === this.rrule && this.workflow.trigger.cadenceLabel) {
            return this.workflow.trigger.cadenceLabel;
        }
        if (this.plan?.cadenceLabel && this._planSignature === this._signature()) {
            return this.plan.cadenceLabel;
        }
        return rruleText(this.rrule);
    }

    get firstRun(): string {
        if (this.workflow && this.workflow.trigger?.rrule === this.rrule && this.workflow.nextRunAt) {
            return formatInTimezone(this.workflow.nextRunAt, this.timezone || null);
        }
        if (this.plan?.nextRunAt && this._planSignature === this._signature()) {
            return formatInTimezone(this.plan.nextRunAt, this.timezone || null);
        }
        return nextOccurrence(this.rrule, this.timezone || null);
    }

    /** A workflow that has already run shows its next run; one that has not is still waiting on its first. */
    get runLabel(): string {
        return this.workflow?.lastRunAt ? 'Next run' : 'First run';
    }

    get summary(): string {
        return this.plan?.summary ?? '';
    }

    /** The customer-facing summary: from the fresh intake when there is one, else the saved workflow. */
    get summaryText(): string {
        return this.plan?.summary ?? this.workflow?.summary ?? '';
    }

    get outcomesSummary(): string {
        return outcomesText(this.buildOutcomes());
    }

    get emailOn(): boolean {
        return this.thenForm.controls.email.value;
    }

    get emailNeedsRecipients(): boolean {
        return this.emailOn && this.recipients.length === 0;
    }

    /** The email outcome is ticked with nobody picked, or the summary promises an email nobody asked for. */
    get thenNotice(): string | null {
        if (this.emailNeedsRecipients || (!this.emailOn && /\bemail/i.test(this.summaryText))) {
            return EMAIL_NOTICE;
        }
        return null;
    }

    get saveDisabled(): boolean {
        return this.isSaving || this.emailNeedsRecipients;
    }

    get recipientIds(): number[] {
        return this.recipients.map((r) => recipientPickerId(r));
    }

    get clarifications(): GableWorkflowClarifications {
        return {
            questions: this._rounds.flatMap((r) => r.batch.questions),
            answers: this._rounds.flatMap((r) => r.answers),
        };
    }

    get selectedSkill(): GableWorkflowSkill | null {
        const name = this._selectedSkillName();
        return name ? (this.skills.find((s) => s.name === name) ?? { name, description: null }) : null;
    }

    get fromSkillName(): string | null {
        return this.selectedSkill?.name ?? null;
    }

    /** The active Do input holds a value; the hidden one never blocks Next. */
    get doReady(): boolean {
        if (this.doMode === 'skill') {
            return !!this.doForm.controls.skillName.value;
        }
        return this.doForm.controls.intent.value.trim() !== '';
    }

    get whenReady(): boolean {
        return this.frequency !== 'weekly' || this.pickersForm.controls.weekdays.value.length > 0;
    }

    get nextDisabled(): boolean {
        return this.intakeBusy || (this.step === 'when' && !this.whenReady) || (this.step === 'do' && !this.doReady);
    }

    get primaryLabel(): string {
        if (this.step !== 'review') {
            return this.intakeBusy ? 'Thinking' : 'Next';
        }
        if (this.isSaving) {
            return 'Saving';
        }
        return this.isEdit ? 'Save changes' : 'Create workflow';
    }

    get showActions(): boolean {
        return !(this.step === 'review' && this.reviewPhase !== 'plan');
    }

    canJump(step: GableWorkflowEditorStep): boolean {
        const index = this.steps.findIndex((s) => s.key === step);
        return index <= this.furthest && !this.intakeBusy && !this.isSaving;
    }

    isDone(step: GableWorkflowEditorStep): boolean {
        const index = this.steps.findIndex((s) => s.key === step);
        return index < this.furthest && step !== this.step;
    }

    goToStep(step: GableWorkflowEditorStep): void {
        if (!this.canJump(step) || step === this.step) {
            return;
        }
        if (step === 'review') {
            if (!this._validateWhen() || !this._validateDo() || !this._validateThen()) {
                return;
            }
            if (this._needsIntake()) {
                this._runFreshIntake();
                return;
            }
            this.reviewPhase = 'plan';
        }
        this.step = step;
        this._focusStep();
    }

    next(): void {
        if (this.intakeBusy) {
            return;
        }
        if (this.step === 'when') {
            if (!this._validateWhen()) {
                return;
            }
            this._advance('do');
            return;
        }
        if (this.step === 'do') {
            if (!this._validateDo()) {
                return;
            }
            this._advance('then');
            return;
        }
        if (this.step === 'then') {
            if (!this._validateThen()) {
                return;
            }
            if (!this._needsIntake()) {
                this._advance('review');
                this.reviewPhase = 'plan';
                return;
            }
            this._runFreshIntake();
        }
    }

    back(): void {
        const index = this.stepIndex;
        if (index <= 0) {
            this.cancel.emit();
            return;
        }
        this.step = this.steps[index - 1].key;
        this._focusStep();
    }

    onCancel(): void {
        this.cancel.emit();
    }

    /** The round joins `_rounds` only once core accepts it, so a failed request never doubles its questions on retry. */
    onAnswers(answers: GableQuestionAnswer[]): void {
        if (!this.batch || this.intakeBusy) {
            return;
        }
        const round: ClarificationRound = { batch: this.batch, answers };
        const rounds = [...this._rounds, round];
        this._intake({
            ...this._intakeBody(),
            draftId: this._draftId ?? undefined,
            questions: rounds.flatMap((r) => r.batch.questions),
            answers: rounds.flatMap((r) => r.answers),
        }, round);
    }

    backToThen(): void {
        this._rounds = [];
        this._draftId = null;
        this.batch = null;
        this.step = 'then';
        this._focusStep();
    }

    useSkillMode(): void {
        this.doMode = 'skill';
        this.doForm.controls.intent.markAsUntouched();
        if (!this._skillsLoaded) {
            this.loadSkills();
        }
        this._focusStep();
    }

    useDescribeMode(): void {
        this.doMode = 'describe';
        this.doForm.controls.skillName.markAsUntouched();
        this._focusStep();
    }

    loadSkills(): void {
        if (this.skillsLoading) {
            return;
        }
        this.skillsLoading = true;
        this.skillsLoadFailed = false;
        this._cdr.markForCheck();
        this._workflows.skills(this.organizationId).pipe(untilDestroyed(this)).subscribe({
            next: ({ skills }) => {
                this.skills = skills ?? [];
                this._skillsLoaded = true;
                this.skillsLoading = false;
                this._cdr.markForCheck();
            },
            error: () => {
                this.skillsLoading = false;
                this.skillsLoadFailed = true;
                this._cdr.markForCheck();
            },
        });
    }

    loadRecipients(): void {
        if (this._recipientByPickerId !== null || this.recipientsLoading) {
            return;
        }
        this.recipientsLoading = true;
        this.recipientsLoadFailed = false;
        this._cdr.markForCheck();
        this._workflows.recipients('', this.organizationId).pipe(untilDestroyed(this)).subscribe({
            next: ({ recipients }) => {
                const loaded = (recipients ?? [])
                    .map((option) => ({ option, recipient: recipientFromOption(option) }))
                    .filter((x): x is { option: GableWorkflowRecipientOption; recipient: GableWorkflowRecipient } => x.recipient !== null);
                const byId = new Map<number, GableWorkflowRecipient>(loaded.map(({ recipient }) => [recipientPickerId(recipient), recipient]));
                const kept = this.recipients.filter((r) => !byId.has(recipientPickerId(r)));
                for (const r of kept) {
                    byId.set(recipientPickerId(r), r);
                }
                this._recipientByPickerId = byId;
                this.recipientPeople = [
                    ...loaded.map(({ option, recipient }) => this._toPerson(recipient, option.isYou === true)),
                    ...kept.map((r) => this._toPerson(r, false)),
                ];
                this.recipientsLoading = false;
                this._preselectHintedRecipient();
                this._cdr.markForCheck();
                setTimeout(() => this._applyPendingRecipientSearch());
            },
            error: () => {
                this.recipientsLoading = false;
                this.recipientsLoadFailed = true;
                this._cdr.markForCheck();
            },
        });
    }

    onRecipientsChange(selections: PickerSelection[]): void {
        const byId = this._recipientByPickerId ?? new Map<number, GableWorkflowRecipient>();
        this.recipients = (selections ?? [])
            .filter((sel): sel is Extract<PickerSelection, { type: 'person' }> => sel.type === 'person')
            .map((sel) => byId.get(Number(sel.person.id)) ?? recipientFromPickerId(Number(sel.person.id), sel.person.name, sel.person.email ?? ''));
        this._cdr.markForCheck();
    }

    buildOutcomes(): GableWorkflowOutcome[] {
        const { suggestions, email } = this.thenForm.getRawValue();
        return normalizeOutcomes([
            ...(suggestions ? [{ type: 'suggestions' } as GableWorkflowOutcome] : []),
            ...(email ? [{ type: 'email', recipients: this.recipients } as GableWorkflowOutcome] : []),
        ]);
    }

    buildPayload(): GableWorkflowInput {
        const skillName = this._selectedSkillName();
        return {
            title: this.reviewForm.controls.title.value.trim(),
            trigger: { type: 'schedule', rrule: this.rrule },
            intent: this._effectiveIntent(),
            plan: this.plan?.plan ?? '',
            summary: this.plan?.summary ?? '',
            outcomes: this.buildOutcomes(),
            ...(skillName ? { skillName } : {}),
            clarifications: this.clarifications,
        };
    }

    buildUpdate(): GableWorkflowUpdate {
        return {
            title: this.reviewForm.controls.title.value.trim(),
            trigger: { type: 'schedule', rrule: this.rrule },
            outcomes: this.buildOutcomes(),
            skillName: this._selectedSkillName(),
            ...(this.plan ? { intent: this._effectiveIntent(), plan: this.plan.plan, summary: this.plan.summary } : {}),
        };
    }

    save(): void {
        if (this.isSaving || this.step !== 'review' || this.reviewPhase !== 'plan') {
            return;
        }
        this.reviewForm.markAllAsTouched();
        if (this.reviewForm.controls.title.invalid) {
            this._growler.oops('Give the workflow a title.');
            return;
        }
        if (!this._validateWhen() || !this._validateDo() || !this._validateThen()) {
            return;
        }
        if (this._needsIntake()) {
            this._runFreshIntake();
            return;
        }
        const request$: Observable<{ workflow: GableWorkflow }> = this.isEdit && this.workflow
            ? this._workflows.update(this.workflow.id, this.buildUpdate(), this.organizationId)
            : this._workflows.create(this.buildPayload(), this.organizationId);

        this.isSaving = true;
        this._cdr.markForCheck();
        request$.pipe(
            finalize(() => {
                this.isSaving = false;
                this._cdr.markForCheck();
            }),
            untilDestroyed(this),
        ).subscribe({
            next: ({ workflow }) => {
                this._growler.success(this.isEdit ? 'Workflow saved' : 'Workflow created', workflow.trigger?.cadenceLabel || this.cadenceText);
                this.saved.emit(workflow);
            },
            error: (err: HttpErrorResponse) => {
                this._growler.fromErrorResponse(err, 'Could not save the workflow.');
            },
        });
    }

    trackByValue(_index: number, item: { value: string | number }): string | number {
        return item.value;
    }

    trackBySkillName(_index: number, skill: GableWorkflowSkill): string {
        return skill.name;
    }

    trackByStep(_index: number, s: { key: GableWorkflowEditorStep }): string {
        return s.key;
    }

    private _validateWhen(): boolean {
        const noWeekday = this.frequency === 'weekly' && this.pickersForm.controls.weekdays.value.length === 0;
        if (noWeekday) {
            this._growler.oops('Pick at least one day of the week.');
            return false;
        }
        if (this.pickersForm.controls.time.invalid) {
            this._growler.oops('Pick a time of day.');
            return false;
        }
        return true;
    }

    private _validateDo(): boolean {
        if (this.doMode === 'skill') {
            this.doForm.controls.skillName.markAsTouched();
            if (!this.doForm.controls.skillName.value) {
                this._growler.oops('Choose a skill, or describe the work instead.');
                return false;
            }
            return true;
        }
        this.doForm.markAllAsTouched();
        if (this.doForm.controls.intent.invalid) {
            this._growler.oops('Tell Gable what to do each time.');
            return false;
        }
        return true;
    }

    private _validateThen(): boolean {
        const { suggestions, email } = this.thenForm.getRawValue();
        if (!suggestions && !email) {
            this._growler.oops('Choose at least one thing to happen after each run.');
            return false;
        }
        if (email && this.recipients.length === 0) {
            this._growler.oops('Pick at least one person to email.');
            return false;
        }
        return true;
    }

    private _advance(step: GableWorkflowEditorStep): void {
        const index = this.steps.findIndex((s) => s.key === step);
        this.furthest = Math.max(this.furthest, index);
        this.step = step;
        this._focusStep();
    }

    private _selectedSkillName(): string | null {
        const skillName = this.doForm.controls.skillName.value;
        return this.doMode === 'skill' && skillName ? skillName : null;
    }

    /** Skill mode has no typed intent, so the box gets the skill's description, or its name. */
    private _effectiveIntent(): string {
        if (this.doMode === 'skill') {
            const skill = this.selectedSkill;
            return (skill?.description ?? '').trim() || skill?.name || '';
        }
        return this.doForm.controls.intent.value.trim();
    }

    /** Intent, skill, trigger or outcome types moved since the plan was written, so plan and summary must be refreshed. */
    private _needsIntake(): boolean {
        return this._planSignature === null || this._planSignature !== this._signature();
    }

    private _runFreshIntake(): void {
        this._rounds = [];
        this._draftId = null;
        this._intake(this._intakeBody());
    }

    /** Recipients stay out on purpose: picking or preselecting people after the plan is not a reason to plan again. */
    private _signature(): string {
        return JSON.stringify({
            intent: this._effectiveIntent(),
            skillName: this._selectedSkillName(),
            rrule: this.rrule,
            outcomeTypes: this.buildOutcomes().map((o) => o.type),
        });
    }

    private _intakeBody(): GableWorkflowIntakeBody {
        const skillName = this._selectedSkillName();
        return {
            intent: this._effectiveIntent(),
            trigger: { type: 'schedule', rrule: this.rrule },
            outcomes: this.buildOutcomes(),
            ...(skillName ? { skillName } : {}),
        };
    }

    /** With a `round`, the question card stays mounted holding the user's answers and the waiting title sits under it. */
    private _intake(body: GableWorkflowIntakeBody, round?: ClarificationRound): void {
        this.intakeBusy = true;
        this._startWaiting(round ? 'Writing your plan' : 'Thinking about your request', round !== undefined);
        this._cdr.markForCheck();
        this._workflows.intake(body, this.organizationId).pipe(
            finalize(() => {
                this.intakeBusy = false;
                this._stopWaiting();
                this._cdr.markForCheck();
            }),
            untilDestroyed(this),
        ).subscribe({
            next: (res) => {
                if (round) {
                    this._rounds = [...this._rounds, round];
                }
                this._viewBeforeIntake = null;
                this._draftId = res.draftId;
                if (res.phase === 'questions') {
                    this.batch = { batchId: res.draftId, leadIn: res.understanding, questions: res.questions };
                    this.reviewPhase = 'questions';
                    this._advance('review');
                    return;
                }
                this._applyPlan(res);
            },
            error: (err: HttpErrorResponse) => {
                if (this._viewBeforeIntake) {
                    this.step = this._viewBeforeIntake.step;
                    this.reviewPhase = this._viewBeforeIntake.reviewPhase;
                    this._viewBeforeIntake = null;
                }
                this._growler.fromErrorResponse(err, 'Gable could not plan this workflow right now. Try again in a moment.');
                this._focusStep();
            },
        });
    }

    /** The Review body shows the waiting card while the box works; the view it replaced comes back on error. */
    private _startWaiting(title: string, keepQuestions: boolean): void {
        this.waitingTitle = title;
        this.waitingHintIndex = 0;
        this._stopHintTimer();
        if (!keepQuestions) {
            this._viewBeforeIntake = { step: this.step, reviewPhase: this.reviewPhase };
            this.reviewPhase = 'waiting';
            this.step = 'review';
            const reduceMotion = typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
            if (!reduceMotion) {
                this._hintTimer = setInterval(() => {
                    this.waitingHintIndex = (this.waitingHintIndex + 1) % WAITING_HINTS.length;
                    this._cdr.markForCheck();
                }, WAITING_HINT_MS);
            }
        }
        this._cdr.markForCheck();
        setTimeout(() => this._el.nativeElement.querySelector<HTMLElement>('.waiting-title')?.focus());
    }

    private _stopWaiting(): void {
        this._stopHintTimer();
    }

    private _stopHintTimer(): void {
        if (this._hintTimer !== null) {
            clearInterval(this._hintTimer);
            this._hintTimer = null;
        }
    }

    private _applyPlan(plan: GableWorkflowIntakePlan): void {
        this.plan = plan;
        const hints = plan.outcomeHints;
        if (hints?.emailRequested && !this.thenForm.controls.email.value) {
            this._pendingRecipientSearch = (hints.recipientsText ?? '').trim() || null;
            this.thenForm.controls.email.setValue(true);
            this._preselectHintedRecipient();
        }
        this._planSignature = this._signature();
        if (!this.isEdit || !this.reviewForm.controls.title.value.trim()) {
            this.reviewForm.controls.title.setValue(plan.title);
        }
        this.reviewPhase = 'plan';
        this._advance('review');
    }

    /** Exactly one loaded person matches the box's recipient text: pick them; otherwise leave the search to the picker. */
    private _preselectHintedRecipient(): void {
        const text = this._pendingRecipientSearch?.toLowerCase();
        if (!text || !this.recipientPeople || this.recipients.length > 0) {
            return;
        }
        const matches = this.recipientPeople.filter((p) => `${p.name} ${p.email ?? ''}`.toLowerCase().includes(text));
        const hit = matches.length === 1 ? this._recipientByPickerId?.get(Number(matches[0].id)) : undefined;
        if (hit) {
            this.recipients = [hit];
            this._pendingRecipientSearch = null;
        }
    }

    private _applyPendingRecipientSearch(): void {
        const text = this._pendingRecipientSearch;
        if (!text || !this._picker) {
            return;
        }
        this._picker.searchControl.setValue(text);
        this._pendingRecipientSearch = null;
        this._cdr.markForCheck();
    }

    private _toPerson(r: GableWorkflowRecipient, isYou: boolean): PickerPerson {
        const initials = r.name.split(' ').map((n) => n[0]).filter(Boolean).join('').toUpperCase().slice(0, 2);
        return {
            id: recipientPickerId(r),
            name: isYou ? `${r.name} (you)` : r.name,
            initials,
            initialsColor: PICKER_AVATAR_COLOR,
            meta: isYou ? `You, ${r.email}` : r.email,
            email: r.email,
        };
    }

    private _focusStep(): void {
        this._cdr.markForCheck();
        setTimeout(() => {
            const host = this._el.nativeElement;
            (host.querySelector<HTMLElement>(FOCUS_SELECTOR) ?? host.querySelector<HTMLElement>(FOCUS_FALLBACK_SELECTOR))?.focus();
            if (this.step === 'then') {
                this._applyPendingRecipientSearch();
            }
        });
    }
}
